<template>
  <PageHeader :title="$t('ui.entities.dashboards.callCenter')">
    <template #right>
      <div class="flex gap-4">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          value-key="id"
          label-key="name"
          :placeholder="`Select a Call Center`"
          class="min-w-52"
          :readonly="callCentersAllowed.length == 1"
          @select="onCallCenterChange"
        ></Select>
        <DatePicker
          v-model="dateRange"
          selection-mode="range"
          show-presets
          :presets="dateRangePresets"
          :months-visible="useBreakPoints({ xs: 1, sm: 2 })"
          class="min-w-60"
          @select="onDateRangeChange"
        />
      </div>
    </template>
  </PageHeader>

  <div v-if="callCenterSelected">
    <div class="grid grid-cols-7 gap-x-6 w-full">
      <!-- Loading State -->
      <template v-if="callCenterRequest.isLoading.value">
        <Card class="col-span-3 flex flex-grow">
          <CardBody>
            <div class="flex justify-around">
              <div class="flex flex-col items-center gap-2">
                <Skeleton
                  shape="circle"
                  size="48"
                />
                <Skeleton
                  width="120px"
                  height="16px"
                />
                <Skeleton
                  width="80px"
                  height="24px"
                />
              </div>
              <div class="flex flex-col items-center gap-2">
                <Skeleton
                  shape="circle"
                  size="48"
                />
                <Skeleton
                  width="120px"
                  height="16px"
                />
                <Skeleton
                  width="80px"
                  height="24px"
                />
              </div>
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-4">
          <CardBody>
            <div class="flex justify-around">
              <div class="flex flex-col items-center gap-2">
                <Skeleton
                  shape="circle"
                  size="120"
                />
                <Skeleton
                  width="100px"
                  height="16px"
                />
              </div>
              <div class="flex flex-col items-center gap-2">
                <Skeleton
                  shape="circle"
                  size="120"
                />
                <Skeleton
                  width="120px"
                  height="16px"
                />
              </div>
              <div class="flex flex-col items-center gap-2">
                <Skeleton
                  shape="circle"
                  size="120"
                />
                <Skeleton
                  width="110px"
                  height="16px"
                />
              </div>
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-2">
          <CardBody>
            <div class="flex justify-between mb-4">
              <Skeleton
                width="80px"
                height="24px"
              />
              <Skeleton
                width="60px"
                height="24px"
              />
            </div>
            <div class="max-w-40 m-auto pb-4">
              <Skeleton
                shape="circle"
                size="180"
              />
            </div>
            <div class="grid grid-cols-2 px-8 gap-2">
              <Skeleton
                width="100%"
                height="20px"
              />
              <Skeleton
                width="100%"
                height="20px"
              />
              <Skeleton
                width="100%"
                height="20px"
              />
              <Skeleton
                width="100%"
                height="20px"
              />
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-2">
          <CardBody>
            <div class="flex justify-between mb-4">
              <Skeleton
                width="80px"
                height="24px"
              />
              <Skeleton
                width="60px"
                height="24px"
              />
            </div>
            <div class="max-w-40 m-auto pb-4">
              <Skeleton
                shape="circle"
                size="180"
              />
            </div>
            <div class="grid grid-cols-2 px-8 gap-2">
              <Skeleton
                width="100%"
                height="20px"
              />
              <Skeleton
                width="100%"
                height="20px"
              />
              <Skeleton
                width="100%"
                height="20px"
              />
              <Skeleton
                width="100%"
                height="20px"
              />
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-3">
          <CardBody>
            <Skeleton
              width="140px"
              height="24px"
              class="mb-4"
            />
            <div class="flex gap-2 flex-wrap">
              <Skeleton
                width="80px"
                height="32px"
                :border-radius="'var(--rounded-full)'"
              />
              <Skeleton
                width="100px"
                height="32px"
                :border-radius="'var(--rounded-full)'"
              />
              <Skeleton
                width="90px"
                height="32px"
                :border-radius="'var(--rounded-full)'"
              />
              <Skeleton
                width="110px"
                height="32px"
                :border-radius="'var(--rounded-full)'"
              />
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-2">
          <CardBody>
            <Skeleton
              width="160px"
              height="24px"
              class="mb-4"
            />
            <Skeleton
              width="100%"
              height="300px"
            />
          </CardBody>
        </Card>
        <Card class="col-span-2">
          <CardBody>
            <Skeleton
              width="140px"
              height="24px"
              class="mb-4"
            />
            <Skeleton
              width="100%"
              height="180px"
            />
          </CardBody>
        </Card>
        <Card class="col-span-3">
          <CardBody>
            <Skeleton
              width="140px"
              height="24px"
              class="mb-4"
            />
            <Skeleton
              width="100%"
              height="180px"
            />
          </CardBody>
        </Card>
      </template>
      <!-- Actual Content -->
      <template v-else>
        <Card class="col-span-3 flex flex-grow">
          <CardBody>
            <div class="flex justify-around">
              <Statistic
                v-model="callStatsComputed.avgVoicemailLength"
                label="Avg Voicemail Length"
                icon="voicemail"
                :show-difference="false"
              />
              <Statistic
                v-model="callStatsComputed.avgTalkingTime"
                label="Avg Talking Time"
                icon="user-clock"
                :show-difference="false"
              />
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-4">
          <CardBody>
            <div class="flex justify-around">
              <RadialOdometerChart
                v-model="callStatsComputed.avgQueueTime"
                label="Avg queue time"
                :break-points="[120, 300]"
                reverse-break-points
                reverse-dial
              >
                {{ dayjs.duration(callStatsComputed.avgQueueTime, 'seconds').format('mm:ss') }}
              </RadialOdometerChart>
              <RadialOdometerChart
                v-model="callStatsComputed.longestQueueTime"
                label="Longest queue time"
                :break-points="[120, 300]"
                reverse-break-points
                reverse-dial
              >
                {{ dayjs.duration(callStatsComputed.longestQueueTime, 'seconds').format('mm:ss') }}
              </RadialOdometerChart>
              <RadialOdometerChart
                v-model="callStatsComputed.avgWrapUpTime"
                label="Avg wrap-up time"
                :break-points="[120, 240]"
                reverse-break-points
                reverse-dial
              >
                {{ dayjs.duration(callStatsComputed.avgWrapUpTime, 'seconds').format('mm:ss') }}
              </RadialOdometerChart>
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-2">
          <CardBody>
            <div class="flex justify-between">
              <CardTitle title="Inbound" />
              <!-- TODO: convert to computed value -->
              <div>
                {{ formatNumber(inboundCallsComputed.length ? inboundCallsComputed.reduce((a, b) => a + b) : 0) }}
              </div>
            </div>
            <div class="max-w-40 m-auto pb-4">
              <apexchart
                v-if="inboundCallsComputed"
                type="donut"
                height="180"
                :options="inboundCallsOptions"
                :series="inboundCallsComputed"
              ></apexchart>
            </div>
            <div class="grid grid-cols-2 px-8">
              <div class="flex gap-2 items-center text-sm">
                <div
                  class="w-4 h-4 rounded-full"
                  style="background-color: var(--green-600)"
                ></div>
                <div>Completed</div>
              </div>
              <div class="flex gap-2 items-center text-sm">
                <div
                  class="w-4 h-4 rounded-full"
                  style="background-color: var(--red-600)"
                ></div>
                <div>Missed</div>
              </div>
              <div class="flex gap-2 items-center text-sm">
                <div
                  class="w-4 h-4 rounded-full"
                  style="background-color: var(--warning-500)"
                ></div>
                <div>Abandoned</div>
              </div>
              <div class="flex gap-2 items-center text-sm">
                <div class="bg-secondary-solid w-4 h-4 rounded-full"></div>
                <div>Failed</div>
              </div>
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-2">
          <CardBody>
            <div class="flex justify-between">
              <CardTitle title="Outbound" />
              <div>
                {{ formatNumber(outboundCallsComputed.length ? outboundCallsComputed?.reduce((a, b) => a + b) : 0) }}
              </div>
            </div>
            <div class="max-w-40 m-auto pb-4">
              <apexchart
                type="donut"
                height="180"
                :options="outboundCallsOptions"
                :series="outboundCallsComputed"
              ></apexchart>
            </div>
            <div class="grid grid-cols-2 px-8">
              <div class="flex gap-2 items-center text-sm">
                <div
                  class="w-4 h-4 rounded-full"
                  style="background-color: var(--green-600)"
                ></div>
                <div>Completed</div>
              </div>
              <div class="flex gap-2 items-center text-sm">
                <div
                  class="w-4 h-4 rounded-full"
                  style="background-color: var(--red-600)"
                ></div>
                <div>No Answer</div>
              </div>
              <div class="flex gap-2 items-center text-sm">
                <div
                  class="w-4 h-4 rounded-full"
                  style="background-color: var(--warning-500)"
                ></div>
                <div>Abandoned</div>
              </div>
              <div class="flex gap-2 items-center text-sm">
                <div class="bg-secondary-solid w-4 h-4 rounded-full"></div>
                <div>Failed</div>
              </div>
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-3">
          <CardBody>
            <CardTitle title="Most Used Tags" />
            <div class="flex gap-2 flex-wrap">
              <Chip
                v-for="tag in callCenterData?.tags"
                :key="tag.tag"
                :label="tag.tag"
              />
            </div>
          </CardBody>
        </Card>
        <Card class="col-span-2">
          <CardBody>
            <CardTitle title="Avg. Attempted Calls" />
            <apexchart
              type="heatmap"
              :options="callsOptions"
              :series="hourlyVolumesComputed"
            ></apexchart>
          </CardBody>
        </Card>
        <Card class="col-span-2">
          <CardBody>
            <div class="flex justify-between">
              <CardTitle title="Avg. Queue Times" />
              <div class="text-sm text-quaternary">Last 6 months</div>
            </div>
          </CardBody>
          <apexchart
            type="area"
            height="180"
            :options="averageQueueTimesOptions"
            :series="monthlyQueueTimesComputed.series"
          ></apexchart>
        </Card>
        <Card class="col-span-3">
          <CardBody>
            <CardTitle title="Reasons for Call" />
            <apexchart
              type="bar"
              height="180"
              :options="reasonsForCallComputed.options"
              :series="reasonsForCallComputed.series"
            ></apexchart>
          </CardBody>
        </Card>
      </template>
    </div>
  </div>
  <Empty
    v-else-if="!callCentersAllowed.length"
    title="No call centers"
    description="You haven't been added to any call centers"
    icon="headset"
  />
  <Empty
    v-else
    title="No call center selected"
    description="Select a call center"
    icon="headset"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import Select from '@/components/forms/Select.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Empty from '@/components/empty/Empty.vue'
import RadialOdometerChart from '@/components/dashboard/charts/radialOdometerChart.vue'
// stores
import { useCallCenterStore } from '@/stores/CallCenterStore'
import { useUserSettingsStore } from '@/stores/UserSettingsStore'
import useApiRequest from '@/composables/useApiRequest'
import dayjs from 'dayjs'
import { useBreakPoints } from '@/composables/useWindowSize'
import { alertToast } from '@/utilities/notification'
import Statistic from '@/components/dashboard/stats/Statistic.vue'
import Chip from '@/components/chip/Chip.vue'
import { formatNumber } from '@/utilities/number'
import Skeleton from '@/components/skeleton/Skeleton.vue'

const callCenterStore = useCallCenterStore()
const userSettingsStore = useUserSettingsStore()

const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(null)

const callCenterData = ref(null)
const callCenterRequest = useApiRequest()

if (!localStorage.getItem('report-filter-call-center-selected')) {
  localStorage.setItem('report-filter-call-center-selected', callCentersAllowed[0]?.id)
  callCenterSelected.value = callCentersAllowed[0]?.id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem('report-filter-call-center-selected'))
}

const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().startOf('day').format('YYYY-MM-DD')])
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  },
  {
    label: 'Last Month',
    value: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month')
    }
  }
]

function onDateRangeChange() {
  fetchData()
}

function onCallCenterChange() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
  fetchData()
}

function fetchData() {
  try {
    callCenterRequest
      .send({
        method: 'GET',
        endpoint: '/v1/dashboards/call-center',
        params: { callCenterId: callCenterSelected.value, startDate: dateRange.value[0], endDate: dateRange.value[1] }
      })
      .then(response => {
        if (response.success) {
          callCenterData.value = response.data
        } else {
          alertToast('Failed to fetch call centre data', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch call centre data', err.message, 'error')
  }
}
fetchData()

const inboundCallsComputed = computed(() => {
  if (!callCenterData.value?.inboundCalls.length) {
    return []
  }
  const callsCount = { completed: 0, missed: 0, abandoned: 0, failed: 0 }
  callCenterData.value?.inboundCalls.forEach(day => {
    callsCount.completed += day.completed
    callsCount.missed += day.missed
    callsCount.abandoned += day.abandoned
    callsCount.failed += day.failed
  })

  return Object.keys(callsCount).map(key => callsCount[key])
})

const outboundCallsComputed = computed(() => {
  if (!callCenterData.value?.outboundCalls.length) {
    return []
  }
  const callsCount = { attempted: 0, opportunity: 0, no_answer: 0, abandoned: 0, failed: 0 }
  callCenterData.value?.outboundCalls.forEach(day => {
    callsCount.attempted += day.attempted
    callsCount.opportunity += day.opportunity
    callsCount.no_answer += day.no_answer
    callsCount.abandoned += day.abandoned
    callsCount.failed += day.failed
  })
  return Object.keys(callsCount).map(key => callsCount[key])
})

const callStatsComputed = computed(() => {
  const avgTalkingTime = callCenterData.value?.callStats?.avgTalkingTime
    ? dayjs.duration(callCenterData.value?.callStats?.avgTalkingTime, 'seconds').format('mm:ss')
    : null
  const avgVoicemailLength = callCenterData.value?.callStats?.avgVoicemailLength
    ? dayjs.duration(callCenterData.value?.callStats?.avgVoicemailLength, 'seconds').format('mm:ss')
    : null
  const avgQueueTime = callCenterData.value?.queueStats?.avgQueueTime ?? null
  const longestQueueTime = callCenterData.value?.queueStats?.longestQueueTime ?? null
  const avgWrapUpTime = callCenterData.value?.callStats?.avgWrapUpTime ?? null
  return { avgTalkingTime, avgQueueTime, avgVoicemailLength, longestQueueTime, avgWrapUpTime }
})

const monthlyQueueTimesComputed = computed(() => {
  const series = [
    {
      name: 'avg',
      data: callCenterData.value?.monthlyQueueTimes.map(item => item.avg_queue_time)
    }
  ]
  const months = callCenterData.value?.monthlyQueueTimes.map(item => item.month)
  const formattedMonths = months?.map(month => dayjs(month).format('MMM')) || []
  return { series, months, formattedMonths }
})

const hourlyVolumesComputed = computed(() => {
  if (!callCenterData.value?.hourlyVolumes) return []

  // helper to get weekday string
  const getWeekday = dateStr => {
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short' })
  }

  const grouped = {}

  callCenterData.value.hourlyVolumes.forEach(day => {
    const weekday = getWeekday(day.date)

    if (!grouped[weekday]) {
      grouped[weekday] = {}
    }

    // only keep hours 9–19 (exclusive of 8 and 20)
    day.hours
      .filter(h => h.hour > 8 && h.hour < 20)
      .forEach(h => {
        if (!grouped[weekday][h.hour]) {
          grouped[weekday][h.hour] = 0
        }
        grouped[weekday][h.hour] += h.count
      })
  })

  // convert to ApexCharts format
  return Object.entries(grouped).map(([weekday, hoursObj]) => ({
    name: weekday,
    data: Object.entries(hoursObj).map(([hour, count]) => ({
      x: hour.toString(),
      y: count
    }))
  }))
})

const reasonsForCallComputed = computed(() => {
  if (!callCenterData.value?.reasons) return { series: [], options: {} }

  const data = callCenterData.value.reasons.map(r => ({
    x: r.reason,
    y: r.count
  }))

  return {
    series: [
      {
        name: 'Count',
        data: data
      }
    ],
    options: {
      chart: {
        sparkline: {
          enabled: false
        },
        parentHeightOffset: 0,
        type: 'bar',
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '80%',
          startingShape: 'rounded',
          endingShape: 'rounded',
          borderRadius: 6
        }
      },
      colors: ['var(--bg-brand-solid)'],
      grid: {
        show: true,
        padding: {
          top: 0,
          left: 0,
          bottom: 0,
          right: 0
        }
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: `var(--text-secondary)`
          },
          show: true
        }
      },
      yaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: `var(--text-secondary)`
          },
          show: true
        }
      }
    }
  }
})

const averageQueueTimesOptions = computed(() => ({
  chart: {
    sparkline: {
      enabled: true
    },
    parentHeightOffset: 0,
    toolbar: { show: false }
  },
  plotOptions: {},
  colors: ['#5bc0de'],
  grid: {
    show: false,
    padding: {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0
    }
  },
  dataLabels: { enabled: false },
  tooltip: {
    custom: function ({ series, dataPointIndex }) {
      const monthValue = monthlyQueueTimesComputed.value.months[dataPointIndex]
      const month = dayjs(monthValue).format('MMM')
      const queueTimeSeconds = series[0][dataPointIndex]
      const formattedTime = dayjs.duration(queueTimeSeconds, 'seconds').format('mm:ss')
      return `
        <div class="px-3 py-2">
          <div class="font-semibold text-sm mb-1">${month}</div>
          <div class="text-xs text-gray-600">Avg. Queue Time: <span class="font-semibold">${formattedTime}</span></div>
        </div>
      `
    }
  },
  legend: { show: false },
  xaxis: {
    categories: monthlyQueueTimesComputed.value.formattedMonths,
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: {
      style: {
        colors: `var(--text-secondary)`
      },
      show: false
    }
  },
  yaxis: { labels: { show: false } }
}))

const inboundCallsOptions = {
  chart: {
    type: 'donut'
  },
  noData: {
    text: 'No data available',
    align: 'center',
    verticalAlign: 'middle',
    style: {
      color: '#999',
      fontSize: '14px'
    }
  },
  labels: ['Completed', 'Missed', 'Abandoned', 'Failed'],
  colors: ['#4CA30D', '#D21C3C', '#E89110', '#475467'],
  stroke: {
    show: false,
    width: 0
  },
  legend: {
    show: false
  },
  dataLabels: {
    enabled: false,
    dropShadow: {
      enabled: false
    },
    formatter: function (val, opts) {
      return opts.w.config.series[opts.seriesIndex] // shows raw value
    }
  },
  plotOptions: {
    pie: {
      donut: {
        size: '70%',
        labels: {
          show: false,
          total: {
            show: false
          },
          value: {
            formatter: function (val, _w) {
              return val
            }
          }
        }
      }
    }
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }
  ]
}

const outboundCallsOptions = {
  chart: {
    type: 'donut'
  },
  noData: {
    text: 'No data available',
    align: 'center',
    verticalAlign: 'middle',
    style: {
      color: '#999',
      fontSize: '14px'
    }
  },
  legend: {
    show: false
  },
  labels: ['Completed', 'No Answer', 'Abandoned', 'Failed'],
  colors: ['#4CA30D', '#D21C3C', '#E89110', '#475467'],
  stroke: {
    show: false,
    width: 0
  },
  dataLabels: {
    enabled: false,
    dropShadow: {
      enabled: false
    },
    formatter: function (val, opts) {
      return opts.w.config.series[opts.seriesIndex] // shows raw value
    }
  },
  plotOptions: {
    pie: {
      donut: {
        size: '70%',
        labels: {
          show: false,
          total: {
            show: false
          },
          value: {
            formatter: function (val, _w) {
              return val
            }
          }
        }
      }
    }
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }
  ]
}

/**
 * Gets the effective theme mode for ApexCharts
 * Resolves 'auto' mode by checking system preference or current body class
 * @returns {string} 'light' or 'dark'
 */
const themeMode = computed(() => {
  const theme =
    userSettingsStore.userSettings?.darkmode || JSON.parse(localStorage.getItem('userSettingsLK') ?? '{}')?.darkmode || 'light'

  if (theme === 'auto') {
    // Check system preference first, fallback to body class
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    // Fallback to checking if dark mode is currently applied to the body
    return document.body.classList.contains('dark') ? 'dark' : 'light'
  }

  return theme === 'dark' ? 'dark' : 'light'
})

const callsOptions = computed(() => ({
  theme: {
    mode: themeMode.value
  },
  chart: {
    height: 200,
    parentHeightOffset: 0,
    type: 'heatmap',
    toolbar: {
      show: false
    },
    sparkline: {
      enabled: false
    }
  },
  plotOptions: {
    heatmap: {
      useFillColorAsStroke: true,
      radius: 0
    }
  },
  grid: {
    padding: {
      top: -30
    }
  },
  dataLabels: {
    enabled: false
  },
  colors: ['#5bc0de'],
  xaxis: {
    labels: {
      show: false
    },
    axisTicks: { show: false },
    axisBorder: { show: false }
  }
}))
</script>

<style></style>
